import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useFormatter from '@hooks/useFormatter';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IComboContratoOperadora } from '@model/ContratoOperadora';
import ILinha from '@model/Linha';
import { useLinhaService } from '@services/LinhaService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { LinhaEditarFormulario } from './LinhaEditarFormulario';

export type LinhaEditarModalProps = {
    modal: IModalWindow
    linha: ILinha
    contratoOperadoraCombo: IComboContratoOperadora
    onLinhaEditada: (linha: AxiosResponse<ILinha>) => void
}

export function LinhaEditarModal ({ modal, linha, contratoOperadoraCombo, onLinhaEditada }: LinhaEditarModalProps) {
    const formatter = useFormatter();
    const loading = useLoading();
    const message = useMessage();

    const linhaService = useLinhaService();

    const form =  useForm<Partial<ILinha>>({
        numeroSemMascara: useField(),
        contratoOperadoraId: useField(),
        linhaEmpresa: useField(),
        vozIlimitada: useField()
    });

    const validation = useValidation<ILinha>({
        numeroSemMascara: yup.string().required().length(15),
        contratoOperadoraId: yup.number().required(),
        linhaEmpresa: yup.string().booleanString(),
        vozIlimitada: yup.string().booleanString()
    });

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();
            validation.validate(values)
                .then(() => {
                    loading.start();
                    linhaService.editar(linha.linhaId, values)
                        .then(response => {
                            message.success("Contrato editado com sucesso");
                            modal.close();
                            onLinhaEditada(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);

        }
    }, [form, linha.linhaId, linhaService, loading, message, modal, onLinhaEditada, validation]);

    const handleEnter = React.useCallback(() => {
        if (!loading.isLoading) {
            loading.start();
            linhaService.carregar(linha.linhaId)
                .then(form.handleResponseSetValues)
                .catch(e => {
                    message.showException(e);
                    modal.close();
                })
                .finally(loading.stop);
        }
    }, [form.handleResponseSetValues, linha.linhaId, linhaService, loading, message, modal]);

    return (
        <ModalWindow 
            modal={modal} 
            size="md" 
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle title={`Edição da Linha ${formatter.toDate(linha.numeroSemMascara)} (${linha.linhaId})`} />
            
            <ModalWindowContent>
                <LinhaEditarFormulario
                    form={form}
                    contratoOperadoraCombo={contratoOperadoraCombo}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Editar Linha</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}